import {useEffect, useState} from 'react'
import {Button, Input, InputNumber, Select, Spin, Switch, Upload, message} from 'antd'
import {ArrowLeft, Bell, Cloud, FileArchive, RotateCcw, Save, UploadCloud} from 'lucide-react'
import {useNavigate, useParams} from 'react-router-dom'
import {useTranslation} from 'react-i18next'
import {APIv1} from '../../../api'
import {getAuthHeaders, normalVersionConfig} from '../version.config'
import '../../../forms/AddNewDeviceForm/AddNewDeviceForm.scss'
import './CreateVersion.scss'
import {playFeedbackWave} from "../../../utils/feedbackWave";

const {Dragger} = Upload

const initialFormData = {
    version_number: '',
    version_number_index: null,
    status: 'STABLE',
    is_save_remotely: true,
    is_telegram_notify: true,
    comment: '',
    file: null,
}

const statusOptions = ['STABLE', 'BETA', 'DEVELOPMENT', 'ARCHIVED'].map(
    status => ({
        label: status,
        value: status,
    }),
)

const getPlainVersionNumber = versionNumber => {
    if (!versionNumber) return ''

    const value = String(versionNumber).trim()
    const markdownLinkMatch = value.match(/^\[([^\]]+)]\(.+\)$/)

    return markdownLinkMatch ? markdownLinkMatch[1] : value
}

const getVersionNumberIndex = versionNumber => {
    const plainVersionNumber = getPlainVersionNumber(versionNumber)
    if (!plainVersionNumber) return null

    const index = Number(plainVersionNumber.replaceAll('.', ''))
    return Number.isNaN(index) ? null : index
}

const getNextVersionNumber = versionNumber => {
    const plainVersionNumber = getPlainVersionNumber(versionNumber)
    if (!plainVersionNumber) return ''

    const parts = plainVersionNumber.split('.')
    const lastIndex = parts.length - 1
    const lastVersionPart = Number(parts[lastIndex])

    if (Number.isNaN(lastVersionPart)) return ''

    parts[lastIndex] = String(lastVersionPart + 1).padStart(parts[lastIndex].length, '0')
    return parts.join('.')
}

const getLatestVersion = versions => {
    return versions.reduce((latest, version) => {
        const currentIndex =
            Number(version.version_number_index) ||
            getVersionNumberIndex(version.version_number)
        const latestIndex =
            Number(latest?.version_number_index) ||
            getVersionNumberIndex(latest?.version_number)

        if (!latest || currentIndex > latestIndex) return version

        return latest
    }, null)
}

const getFirstErrorMessage = errorData => {
    if (!errorData) return null
    if (typeof errorData === 'string') return errorData

    if (Array.isArray(errorData)) {
        for (const item of errorData) {
            const message = getFirstErrorMessage(item)
            if (message) return message
        }
        return null
    }

    if (typeof errorData === 'object') {
        for (const value of Object.values(errorData)) {
            const message = getFirstErrorMessage(value)
            if (message) return message
        }
    }

    return null
}

const Field = ({label, required, icon, children, wide}) => (
    <div className={`create-device__field${wide ? ' create-device__field--wide' : ''}`}>
        <div
            className={`create-device__field-label${
                required ? ' create-device__required-label' : ''
            }`}
        >
            {icon && <span className='create-device__field-icon'>{icon}</span>}
            {label}
        </div>
        <div className='create-device__field-control'>{children}</div>
    </div>
)

const SwitchOption = ({label, icon, checked, onChange}) => (
    <div className='create-version__switch-option'>
        <div className='create-version__switch-label'>
            <span className='create-version__switch-icon'>{icon}</span>
            <span>{label}</span>
        </div>
        <Switch checked={checked} onChange={onChange}/>
    </div>
)

// const getFormDataLog = payload => {
// 	const data = {}
//
// 	payload.forEach((value, key) => {
// 		if (value instanceof File) {
// 			data[key] = {
// 				name: value.name,
// 				size: value.size,
// 				type: value.type,
// 			}
// 			return
// 		}
//
// 		data[key] = value
// 	})
//
// 	return data
// }

const CreateVersion = ({config = normalVersionConfig}) => {
    const {t} = useTranslation()
    const navigate = useNavigate()
    const {id} = useParams()
    const [userData, setUserData] = useState({})
    const [formData, setFormData] = useState(initialFormData)
    const [currentFiles, setCurrentFiles] = useState({
        file_name: null,
    })
    const [isLoading, setIsLoading] = useState(false)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const isEditing = Boolean(id)
    const isStaff = Boolean(userData?.data?.is_admin)

    useEffect(() => {
        const items = JSON.parse(localStorage.getItem('user'))
        if (items) {
            setUserData(items)
        }
    }, [])

    useEffect(() => {
        if (!userData.token || isEditing) return

        const fetchVersionDefaults = async () => {
            try {
                const response = await APIv1.get(config.endpoints.list, {
                    headers: getAuthHeaders(userData.token),
                })

                const versions = Array.isArray(response.data)
                    ? response.data
                    : response.data?.results || []
                const latestVersion = getLatestVersion(versions)
                const nextVersionNumber = getNextVersionNumber(
                    latestVersion?.version_number,
                )

                if (!nextVersionNumber) return

                setFormData(prev => ({
                    ...prev,
                    version_number: prev.version_number || nextVersionNumber,
                    version_number_index:
                        prev.version_number_index || getVersionNumberIndex(nextVersionNumber),
                }))
            } catch (error) {
                console.error('Error fetching version defaults:', error)
            }
        }

        fetchVersionDefaults()
    }, [userData.token, isEditing, config])

    useEffect(() => {
        if (!userData.token || !isEditing) return

        if (!isStaff) {
            navigate(config.routes.detail(id))
            return
        }

        const fetchVersionDetail = async () => {
            setIsLoading(true)
            try {
                const response = await APIv1.get(config.endpoints.detail(id), {
                    headers: getAuthHeaders(userData.token),
                })
                const data = response.data

                setFormData({
                    version_number: data.version_number || '',
                    version_number_index: data.version_number_index ?? null,
                    status: data.status || 'STABLE',
                    is_save_remotely: Boolean(data.is_save_remotely),
                    is_telegram_notify: initialFormData.is_telegram_notify,
                    comment: data.comment || '',
                    file: null,
                })
                setCurrentFiles({
                    file_name: data.file_name || null,
                })
            } catch (error) {
                console.error('Error fetching version detail:', error)
                message.error(t('pages.versions.detail.messages.fetchError'))
            } finally {
                setIsLoading(false)
            }
        }

        fetchVersionDetail()
    }, [userData.token, isEditing, isStaff, id, navigate, t, config])

    const updateField = (field, value) => {
        setFormData(prev => ({
            ...prev,
            [field]: value,
        }))
    }

    const handleVersionNumberChange = value => {
        setFormData(prev => ({
            ...prev,
            version_number: value,
            version_number_index: getVersionNumberIndex(value),
        }))
    }

    const createUploadProps = field => ({
        maxCount: 1,
        beforeUpload: file => {
            updateField(field, file)
            return false
        },
        onRemove: () => {
            updateField(field, null)
        },
        fileList: formData[field]
            ? [
                {
                    uid: formData[field].uid || field,
                    name: formData[field].name,
                    status: 'done',
                },
            ]
            : [],
    })

    const validateForm = () => {
        const requiredFields = [
            'version_number',
            'version_number_index',
            'status',
        ]

        if (!isEditing) {
            requiredFields.push('file')
        }

        const hasMissingField = requiredFields.some(field => {
            const value = formData[field]
            return value === null || value === undefined || value === ''
        })

        if (hasMissingField) {
            message.warning(t('pages.versions.create.messages.required'))
            return false
        }

        return true
    }

    const handleSubmit = async () => {
        if (!validateForm()) return
        if (!userData.token) {
            message.error(t('pages.versions.create.messages.authRequired'))
            return
        }

        const payload = new FormData()
        payload.append('version_number', formData.version_number)
        payload.append('version_number_index', formData.version_number_index)
        payload.append('status', formData.status)
        payload.append('is_save_remotely', formData.is_save_remotely)
        payload.append('comment', formData.comment || '')

        if (!isEditing) {
            payload.append('is_telegram_notify', formData.is_telegram_notify)
        }

        if (formData.file) {
            payload.append('file', formData.file)
        }

        // console.log('---> version payload', {
        // 	method: isEditing ? 'PATCH' : 'POST',
        // 	url: isEditing ? config.endpoints.detail(id) : config.endpoints.create,
        // 	data: getFormDataLog(payload),
        // })

        setIsSubmitting(true)
        try {
            const response = isEditing
                ? await APIv1.patch(config.endpoints.detail(id), payload, {
                    headers: {
                        ...getAuthHeaders(userData.token),
                        'Content-Type': 'multipart/form-data',
                    },
                })
                : await APIv1.post(config.endpoints.create, payload, {
                    headers: {
                        ...getAuthHeaders(userData.token),
                        'Content-Type': 'multipart/form-data',
                    },
                })

            if (response.status === 201 || response.status === 200) {
                playFeedbackWave('success');
                message.success(
                    isEditing
                        ? t('pages.versions.detail.messages.updateSuccess')
                        : t('pages.versions.create.messages.success'),
                )
                navigate(isEditing ? config.routes.detail(id) : config.routes.list())
            } else {
                message.info(t('pages.versions.create.messages.info'))
            }
        } catch (error) {
            console.error('Error saving version:', error)
            const serverMessage = getFirstErrorMessage(error?.response?.data)
            const fallbackMessage = isEditing
                ? t('pages.versions.detail.messages.updateError')
                : t('pages.versions.create.messages.error')

            playFeedbackWave('failure');
            message.error(serverMessage || fallbackMessage)
        } finally {
            setIsSubmitting(false)
        }
    }

    const handleClear = () => {
        setFormData(initialFormData)
        message.info(t('pages.versions.create.messages.clear'))
    }

    if (isLoading) {
        return (
            <div className='content_container create-device create-version create-version__loader'>
                <Spin/>
            </div>
        )
    }

    return (
        <div className='content_container create-device create-version'>
            <div className='create-device__header'>
                <div className='create-device__heading'>
					<span className='create-device__eyebrow'>
						{isEditing
                            ? t('pages.versions.detail.edit')
                            : t('pages.versions.create.eyebrow')}
					</span>
                    <h1>
                        {isEditing
                            ? t(config.editTitleKey)
                            : t(config.createTitleKey)}
                    </h1>
                    {isEditing && <span className='create-device__serial'>#{id}</span>}
                </div>
                <Button
                    className='create-device__action-button create-device__action-button--secondary'
                    icon={<ArrowLeft size={16}/>}
                    onClick={() => navigate(-1)}
                >
                    {t('common.backBtn')}
                </Button>
            </div>

            <div className='create-device__forms'>
                <section className='create-device__forms-container'>
                    <div className='create-device__section-header'>
                        <span className='create-device__section-index'>01</span>
                        <h2 className='create-device__forms-title'>
                            {t('pages.versions.create.versionSectionTitle')}
                        </h2>
                    </div>

                    <div className='create-device__field-grid'>
                        <Field label={t('pages.versions.create.fields.versionNumber')} required>
                            <Input
                                className='create-device__control'
                                placeholder='2.4.1'
                                value={formData.version_number}
                                onChange={event => handleVersionNumberChange(event.target.value)}
                            />
                        </Field>

                        <Field label={t('pages.versions.create.fields.versionIndex')} required>
                            <InputNumber
                                className='create-device__control'
                                min={0}
                                precision={0}
                                placeholder='241'
                                value={formData.version_number_index}
                                onChange={value => updateField('version_number_index', value)}
                            />
                        </Field>

                        <Field label={t('pages.versions.create.fields.status')} required>
                            <Select
                                className='create-device__control'
                                options={statusOptions}
                                value={formData.status}
                                onChange={value => updateField('status', value)}
                            />
                        </Field>

                        <Field label={t('pages.versions.create.fields.comment')}>
                            <Input.TextArea
                                className='create-device__control'
                                rows={3}
                                placeholder={t('pages.versions.create.placeholders.comment')}
                                value={formData.comment}
                                onChange={event => updateField('comment', event.target.value)}
                            />
                        </Field>

                    </div>
                </section>

                <section className='create-device__forms-container'>
                    <div className='create-device__section-header'>
                        <span className='create-device__section-index'>02</span>
                        <h2 className='create-device__forms-title'>
                            {t('pages.versions.create.filesSectionTitle')}
                        </h2>
                    </div>

                    <div className='create-version__release-grid'>
                        <Field
                            label={t(config.fileFieldKey)}
                            required={!isEditing}
                            icon={<FileArchive size={16}/>}
                        >
                            <Dragger
                                className='create-version__upload'
                                {...createUploadProps('file')}
                            >
                                <UploadCloud size={28}/>
                                <span>
									{currentFiles.file_name ||
                                        t('pages.versions.create.uploadText')}
								</span>
                            </Dragger>
                        </Field>

                        <div className='create-version__switch-panel'>
                            <SwitchOption
                                label={t('pages.versions.create.fields.isSaveRemotely')}
                                icon={<Cloud size={16}/>}
                                checked={formData.is_save_remotely}
                                onChange={checked => updateField('is_save_remotely', checked)}
                            />

                            {!isEditing && (
                                <SwitchOption
                                    label={t('pages.versions.create.fields.telegramNotify')}
                                    icon={<Bell size={16}/>}
                                    checked={formData.is_telegram_notify}
                                    onChange={checked =>
                                        updateField('is_telegram_notify', checked)
                                    }
                                />
                            )}
                        </div>
                    </div>
                </section>
            </div>

            <div className='create-device__footer'>
				<span className='create-device__footer-copy'>
					{isEditing
                        ? t('pages.versions.detail.editFooter')
                        : t(config.createFooterKey)}
				</span>
                <div className='create-device__footer-buttons'>
                    <Button
                        className='create-device__action-button create-device__action-button--primary'
                        icon={<Save size={16}/>}
                        type='primary'
                        size='large'
                        onClick={handleSubmit}
                        loading={isSubmitting}
                    >
                        {isEditing ? t('pages.versions.detail.save') : t('common.submitBtn')}
                    </Button>

                    <Button
                        type='default'
                        className='create-device__action-button create-device__action-button--secondary'
                        icon={<RotateCcw size={16}/>}
                        size='large'
                        onClick={handleClear}
                    >
                        {t('common.clearBtn')}
                    </Button>
                </div>
            </div>
        </div>
    )
}

export default CreateVersion;
